import { FC, KeyboardEvent, PropsWithChildren } from "react";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import {
  CollapsibleContent,
  CollapsibleWrapper,
  CompletedBadge,
  IconsWrapper,
  SectionBodyWrapper,
  SectionHeaderWrapper,
  SectionTitle,
} from "./addStaffPanel.styled";

interface SectionHeaderProps {
  readonly title: string;
  readonly bodyId: string;
  readonly isOpen: boolean;
  readonly isCompleted: boolean;
  readonly isLocked: boolean;
  readonly onClick: () => void;
}

export const SectionBody = ({
  collapsed,
  id,
  children,
}: PropsWithChildren<{ collapsed: boolean; id: string }>): JSX.Element => {
  return (
    <CollapsibleWrapper $collapsed={collapsed}>
      <CollapsibleContent
        id={id}
        $collapsed={collapsed}
        aria-hidden={collapsed}
      >
        <SectionBodyWrapper>{children}</SectionBodyWrapper>
      </CollapsibleContent>
    </CollapsibleWrapper>
  );
};

/**
 * The header carries the button role itself rather than being one: it holds the
 * employment template menu, and a button inside a button is neither valid nor
 * operable. The whole row stays the target, by pointer and by key alike.
 */
export const SectionHeader: FC<PropsWithChildren<SectionHeaderProps>> = ({
  title,
  bodyId,
  isOpen,
  isCompleted,
  isLocked,
  onClick,
  children,
}) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (isLocked) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    if (event.target !== event.currentTarget) return;
    // Space would otherwise scroll the panel out from under the section.
    event.preventDefault();
    onClick();
  };

  return (
    <SectionHeaderWrapper
      role="button"
      tabIndex={isLocked ? -1 : 0}
      aria-expanded={isOpen}
      aria-controls={bodyId}
      aria-disabled={isLocked}
      $locked={isLocked}
      onClick={isLocked ? undefined : onClick}
      onKeyDown={handleKeyDown}
    >
      <SectionTitle $locked={isLocked}>{title}</SectionTitle>
      {children}
      <IconsWrapper>
        {isCompleted && (
          <CompletedBadge>
            <CustomIcon
              name="v2/checkmark"
              size="24px"
              color={ColorV2.PositiveGreen}
            />
          </CompletedBadge>
        )}
        <CustomIcon
          name={isOpen ? "v2/down-chevron" : "v2/up-chevron"}
          size="24px"
          color={isLocked ? ColorV2.Blue5 : ColorV2.Blue2}
        />
      </IconsWrapper>
    </SectionHeaderWrapper>
  );
};
